// Inbox slot — auto-lead-detect indicator (US-087, EPIC-07).
//
// Renders when:
//   - `account.autoLeadDetect` is on (per-account toggle from the voice editor),
//   - the conversation has been auto-pushed to Leads with `outcomeAuto: true`.
//
// The badge surfaces the silent transition the user might otherwise miss:
// the AI saw a hot signal (calendar / pricing / phone / email / clear intent)
// and added the conv to Leads without explicit user click. Per the spec the
// detection runs server-side (Wave 6) — this UI only reflects state.
//
// Design ref: `prototypes_and_docs/ls-voice.jsx:978-1004` (signals list)
//           + `prototypes_and_docs/ls-inbox.jsx:286-293` (auto-detect side-effect).

import { useT } from '@barel/i18n';
import { Banner, Button } from '@barel/ui';

export interface AutoLeadDetectIndicatorProps {
  visible: boolean;
  /** Optional deep-link to the Leads list, scoped to the lead row. */
  onOpenLeads?: () => void;
}

export function AutoLeadDetectIndicator({ visible, onOpenLeads }: AutoLeadDetectIndicatorProps) {
  const t = useT('inbox');
  if (!visible) return null;
  return (
    <Banner
      data-component="AutoLeadDetectIndicator"
      data-testid="inbox-auto-lead-detect-banner"
      tone="success"
      title={t('policy.autoLeadDetect.title')}
      icon="✦"
      action={
        onOpenLeads ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onOpenLeads}
            data-testid="inbox-auto-lead-detect-open-leads"
          >
            {t('policy.autoLeadDetect.openLeads')}
          </Button>
        ) : undefined
      }
    >
      {t('policy.autoLeadDetect.body')}
    </Banner>
  );
}
